import React, {useEffect} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {Grid, ListItemButton, ListItemIcon, ListItemText} from '@mui/material';
import _ from 'lodash';
import API from '../../../app/api';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {useDispatch, useSelector} from 'react-redux';
import {specialties as specialtiesReducer} from '../../../store/reducers/preferences';
import {sendUserAudit} from '../../../store/reducers/user';
import {setSnackbar} from '../../../store/reducers/globals';
import APIForm from '../../../app/apiForm';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleAccordion from '../../../components/ui/accordion/agyleAccordion';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import SearchIcon from '@mui/icons-material/Search';
import clsx from 'clsx';
import {ctxSplitScreen, setFormIsOpen} from '../../../store/reducers/patientContext';
import {getConfig} from '../../../util/configUtil';
import { AUDIT_TYPES } from '../../../util/auditUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        overflowY: 'hidden',
    },
    container: {
        paddingRight: 5,
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
    },
    accDetails: {
        borderTop: '1px solid ' + theme.palette.divider,
    },
    accordionTitle: {
        fontSize: 15,
        fontWeight: '600',
    },
    secondHeadingStyle: {
        fontSize: 'small',
    },
    accordionSecondLabelContainer: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 10,
    },
    accordionSecondLabel: {
        marginRight: 5,
        borderRadius: 7,
        padding: '3px 10px',
        // backgroundColor: 'black',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary,
    },
    searchField: {
        marginTop: 6,
        marginBottom: 8,
        marginRight: 5,
    },
    searchText: {
        height: 18,
    },
    secondSign: {
        border: '1px solid red',
        margin: '10px',
        borderRadius: 7,
    },
}));

function Forms(props) {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const controller = new AbortController();
    const isMounted = React.useRef(true);

    const splitScreen = useSelector(ctxSplitScreen);

    // Configs
    const formsURL = getConfig('formsURL', '');
    const autoTransfereFromDrafts = getConfig('autoTransfereFromDrafts', false);

    const packData = (data) => {
        function customizer(value, name) {
            if (_.isObject(value) && moment.isMoment(value)) {
                return {
                    __isDate: true,
                    __date: value.toISOString(),
                };
            }
        }

        return _.cloneDeepWith(data, customizer);
    };

    const _patient = props.patient?.patient;
    const patient = {
        patientId: _patient?.__id,
        hospitalNumber: _patient?.hospitalNumber,
        nhsNumber: _patient?.nhsNumber,
        firstName: _patient?.firstName,
        middleNames: _patient?.middleNames,
        familyName: _patient?.familyName,
        dob: moment(_patient?.dob),
        gender: _patient?.gender,
        addresses: _patient?.addressLines,
        phoneNumbers: _patient?.phoneNumbers,
        postalCode: _patient?.postalCode,
    };

    const [openForm, setFormOpen] = React.useState(null);
    const [selectedFormDetails, setSelectedFormDetails] = React.useState(null);

    const [initialized, setInitialized] = React.useState(null);

    const loaded = () => {
        setInitialized(true);
    };

    const src = formsURL + 'frame.html';
    const [formsSRC, setSrc] = React.useState(src);


    const [createdForms, setCreatedForms] = React.useState(null);
    const [loadingForms, setLoadingForms] = React.useState(true);
    const [expanded, setExpanded] = React.useState({});
    const [searchPhrase, setSearchPhrase] = React.useState('');

    const specialties = useSelector(specialtiesReducer);

    const [formNotSaved, setFormNotSaved] = React.useState(false);

    useEffect(() => {
        return () => {
            isMounted.current = false;
            controller.abort();
        };
    }, []);

    useEffect(() => {
        dispatch(setFormIsOpen(!!formNotSaved));
    }, [formNotSaved]);

    useEffect(async () => {
        const handler = event => {
            switch (event.data?.action) {
                case 'touch':
                    window._lastClick = moment().valueOf();
                    setFormNotSaved(true);
                    break;
                case 'saved':
                    setFormNotSaved(false);
                    break;
                case 'close':
                case 'finished':
                    setSrc('');
                    setFormOpen(null);
                    setSelectedFormDetails(null);
                    setFormNotSaved(false);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('message', handler);


        let t = await API.getFormSet(controller.signal);

        if (isMounted.current) {
            setFormsList(t);
        }

        return () => window.removeEventListener('message', handler);
    }, []);

    const activeSpecialty = () => {
        if (specialties && specialties?.length > 0) {
            let spec = _.filter(specialties, {isActive: true});
            if (spec && spec.length > 0) {
                return spec[0].__description;
            }
        }
    };

    const getAttendanceIdsList = () => {
        let attendanceIds = [props.patient.admission.attendanceId];
        if (props.patient.studies?.edau?.transfer?.edAttendanceId && autoTransfereFromDrafts) {
            attendanceIds.push(props.patient.studies.edau.transfer.edAttendanceId);
        }

        return attendanceIds;
    };

    const [attendanceIds] = React.useState(getAttendanceIdsList());

    const selectData = (formData) => {
        let id = (formData && formData.attendanceId) ? formData.attendanceId : props.patient.admission?.attendanceId;
        let ids = props.patient.admission?.attendanceId;

        if (formData && formData.attendanceIds) {
            let attendanceIdsArray = formData.attendanceIds.split(',');

            ids = formData && formData.attendanceIds && attendanceIdsArray.some((v) => v === props.patient.admission?.attendanceId) ?
                formData.attendanceIds : formData.attendanceIds + ',' + props.patient.admission?.attendanceId;
        }

        return {attendId: id, attendanceIds: ids};
    };


    useEffect(() => {
        if (isMounted.current && initialized && openForm) {
            setFormNotSaved(true);
            const theme = window._getTheme();

            let tokens = {
                token: window.auth.token,
                tokenParsed: window.auth.tokenParsed,
            };

            delete openForm.forms;

            const timer = setTimeout(() => {
                const wn = document.getElementById('tifr').contentWindow;
                wn.postMessage({
                    type: 'authorization',
                    authorized: true,
                    ...tokens,
                    formName: openForm.template,
                    formId: selectedFormDetails?.id ? selectedFormDetails.id : null,
                    ...selectData(selectedFormDetails),
                    activeSpec: activeSpecialty(),
                    theme,
                    openFromAgyle: true,
                    patient: packData(patient),
                    customData: {
                        __id: openForm.template,
                        ...openForm,
                    },

                }, '*');
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [openForm, initialized]);

    const [formsList, setFormsList] = React.useState([]);

    const formsButtons = (phrase) => {
        let buttons = [];
        formsList?.forEach((formToOpen, index) => {
            if (formToOpen) {
                const button = (
                  <Grid item xs={12} key={index} sx={{ mb: 1 }}>
                    <AgyleButton
                      startIcon={<NoteAddIcon />}
                      onClick={() => {
                        setInitialized(false);
                        dispatch(
                          sendUserAudit(props.patient, AUDIT_TYPES.OPEN_FORM, {
                            editForm: false,
                            formName: formToOpen?.name,
                            formId: formToOpen?.__id,
                          })
                        );
                        setSrc(src);
                        setFormOpen(formToOpen);
                      }}
                      disabled={props.disableSave}
                      size="large"
                      variant="text"
                      color={
                        openForm?.template === formToOpen?.template
                          ? "primary"
                          : "default"
                      }
                      style={{ width: "100%", textAlign: "left" }}
                      justifyContent={"flex-start"}
                    >
                      {formToOpen.name}
                    </AgyleButton>
                  </Grid>
                );

                if (phrase) {
                    if (String(formToOpen.name).toLowerCase().includes(String(phrase).toLowerCase())) {
                        buttons.push(button);
                    }
                } else {
                    buttons.push(button);
                }
            }
        });


        return _.isEmpty(buttons) ? `No forms ${phrase ? 'with given phrase' : ''} available.` : buttons;
    };

    useEffect(() => {
        getForms();

    }, [formsList, openForm]);

    const getForms = () => {
        let episodeNumber = null;

        if (autoTransfereFromDrafts && props.patient.admission.attendanceType !== 'E')
            episodeNumber = props.patient.admission?.unityEpisodeNumber;

        APIForm.getFormList(props.patient.patient.__id, attendanceIds, episodeNumber, formsList, controller.signal).then((list) => {
            if (!isMounted.current) return;

            setCreatedForms(list);
            setLoadingForms(false);
        }, (er) => {
            dispatch(setSnackbar({type: 'error', message: 'The form list is unavailable.'}));
            setLoadingForms(false);
        });
    };
    const getPrimaryText = (form) => {

        let prText = form.__isClosed ? 'Submitted By: ' + form.__lastModifiedByFullName : 'Created By: ' + form.__createdByFullName;
        if (form.attendanceId === props.patient.studies?.edau?.transfer?.edAttendanceId) {
            prText += ' (Started on ED)';
        }

        return prText;
    };

    const accordionChangeHandler = (accordion) => (ev) => {
        setExpanded((prev) =>
            ({
                ...prev,
                [accordion]: !prev[accordion],
            }),
        );
    };

    const formList = (classes) => {
        let forms = [];
        createdForms?.forEach((formItem, index) => {
            if (formItem.forms.length) {
                let t = [];
                formItem.forms?.forEach((form, indexForm) => {
                    if (form.__status !== 'deleted' && (!form.__isClosed || (form.__isClosed && form.attendanceId === props.patient.admission?.attendanceId))) {
                        t.push(
                            <ListItemButton
                                className={formItem.__id === 'self.discharge' && form.__state.step === 'step2' ? classes.secondSign : null}
                                key={indexForm}
                                disabled={props.disableSave}
                                onClick={() => {
                                    setInitialized(false);
                                    setSelectedFormDetails({
                                        id: form._id,
                                        attendanceId: form.attendanceId,
                                        attendanceIds: form.attendanceIds,
                                    });
                                    dispatch(
                                      sendUserAudit(
                                        props.patient,
                                        AUDIT_TYPES.OPEN_FORM,
                                        {
                                          editForm: true,
                                          formName: formItem?.name,
                                          formId: formItem?.__id,
                                          editFormId: form?._id,
                                        }
                                      )
                                    ); 
                                    setSrc(src);
                                    setFormOpen(formItem);
                                }}
                            >
                                <ListItemIcon>
                                    {form.__isClosed ? <CheckCircleIcon color="success"/> : <EditIcon/>}
                                </ListItemIcon>

                                <ListItemText
                                    primary={getPrimaryText(form)}
                                    secondary={
                                        form.__isClosed ?
                                            ' Submitted: ' + moment(form.__lastModifiedDate.__date).format('DD/MM/YYYY HH:mm') :
                                            ' Created: ' + moment(form.__createdDate.__date).format('DD/MM/YYYY HH:mm')}
                                />
                                {(formItem.__id === 'self.discharge' && form.__state.step !== 'start' && !form.__isClosed) &&
                                    <AgyleButton
                                        startIcon={<NoteAddIcon/>}
                                    >
                                        SECOND SIGNATURE
                                    </AgyleButton>
                                }
                            </ListItemButton>,
                        );
                    }
                });

                if (t.length) {
                    const draftAmount = formItem.forms.filter((form) => form.__isClosed === false).length;
                    const multipleDrafts = draftAmount > 1;
                    const multipleForms = formItem.forms.length > 1;

                    const secondLabel = (
                        <div className={classes.accordionSecondLabelContainer}>
                            <div className={classes.accordionSecondLabel}>
                                {formItem?.forms?.length - draftAmount} submitted
                            </div>

                            <div className={classes.accordionSecondLabel}>
                                {draftAmount} in progress
                            </div>
                        </div>);

                    forms.push(
                        <AgyleAccordion
                            key={index + 'Label'}
                            expanded={expanded[index + 'Label'] ? expanded[index + 'Label'] : false}
                            onChange={accordionChangeHandler(index + 'Label')}
                            label={formItem.name}
                            secondLabel={secondLabel}
                            headingStyle={classes.accordionTitle}
                            secondHeadingStyle={clsx(splitScreen?.enabled && classes.secondHeadingStyle)}
                        >
                            {t}
                        </AgyleAccordion>,
                    );
                }
            }
        });

        if (!forms.length) {
            forms = (
                <div style={{textAlign: 'center', marginTop: 20, textTransform: 'uppercase'}}>
                    {t('No Forms')}
                </div>
            );
        }
        return forms;
    };

    return (
        <div style={{height: '99%'}}>
            {(!openForm || !initialized) &&
                <Grid container spacing={2} sx={{height: '100%'}} className={classes.root}>
                    <Grid item xs={12} className={classes.searchField}>
                        <AgyleTextField
                            onChange={(e) => setSearchPhrase(e.target.value)}
                            size="small"
                            inputPropsClasses={classes.searchText}
                            style={{width: '100%', marginBottom: 8}}
                            placeholder={t('Search form name')}
                            value={searchPhrase}
                            noBorder={false}
                            startAdornment={<SearchIcon style={{marginRight: 8}}/>}
                            shrink
                            smallerPlaceholder
                        />
                    </Grid>

                    <Grid item xs={4} className={clsx(classes.container, 'scroll-normal-white')}
                          style={{paddingTop: 0}}>
                        <Grid container justifyContent="center" style={{paddingBottom: 80}}>
                            {formsButtons(searchPhrase)}
                        </Grid>
                    </Grid>

                    <Grid item xs={8} className={clsx(classes.container, 'scroll-normal-white')}
                          style={{paddingTop: 0, paddingBottom: 80}}>
                        {!loadingForms && formList(classes)}
                    </Grid>
                </Grid>
            }

            <Backdrop
                className={classes.backdrop}
                open={loadingForms || initialized === false}
            >
                <CircularProgress color="inherit" size={64}/>
            </Backdrop>

            {openForm &&
                <iframe title="dynamic-forms" id="tifr" onLoad={loaded} src={formsSRC} width={'100%'} height={'100%'}/>}
        </div>);
}

export default Forms;
